import {
  CheckCircle2,
  XCircle,
  HelpCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { NodeEvaluationTrace } from "@/types/decision-definition-schema";
import { useState } from "react";

interface TraceViewerProps {
  trace: NodeEvaluationTrace[];
}

export function TraceViewer({ trace }: TraceViewerProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          Evaluation Trace
          <Badge variant="secondary" className="text-[10px]">
            {trace.length} nodes
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {trace.map((entry, idx) => (
            <TraceEntry key={idx} entry={entry} index={idx} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function TraceEntry({
  entry,
  index,
}: {
  entry: NodeEvaluationTrace;
  index: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const stateConfig = getTriStateConfig(entry.triState);

  return (
    <div className="px-3 py-1.5 hover:bg-muted/30 transition-colors">
      <div
        className="flex items-center gap-2 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
        )}

        <stateConfig.icon
          className={cn("h-3.5 w-3.5 shrink-0", stateConfig.color)}
        />

        <span className="text-[10px] text-muted-foreground font-mono shrink-0 w-5 text-right">
          {index + 1}
        </span>

        <Badge
          variant="outline"
          className={cn(
            "text-[10px] shrink-0 px-1 py-0",
            stateConfig.badgeClass
          )}
        >
          {entry.nodeType}
        </Badge>

        <span className="text-xs truncate flex-1 min-w-0">
          {entry.label || entry.nodeId || "—"}
        </span>

        {entry.result !== undefined && entry.result !== null && (
          <span className="text-[10px] font-mono text-muted-foreground shrink-0 max-w-[120px] truncate">
            → {formatValue(entry.result)}
          </span>
        )}

        {entry.missingFacts && entry.missingFacts.length > 0 && (
          <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />
        )}
      </div>

      {isExpanded && (
        <div className="ml-8 mt-1.5 mb-1 space-y-1">
          {entry.nodeId && (
            <div className="text-[10px]">
              <span className="text-muted-foreground">Node ID:</span>{" "}
              <code className="bg-muted px-1 rounded">{entry.nodeId}</code>
            </div>
          )}

          <div className="text-[10px]">
            <span className="text-muted-foreground">Type:</span>{" "}
            {entry.nodeType}
          </div>

          <div className="text-[10px]">
            <span className="text-muted-foreground">Tri-state:</span>{" "}
            <span className={stateConfig.color}>
              {entry.triState || "—"}
            </span>
          </div>

          {entry.result !== undefined && (
            <div className="text-[10px]">
              <span className="text-muted-foreground">Value:</span>{" "}
              <code className="bg-muted px-1 rounded font-mono">
                {JSON.stringify(entry.result)}
              </code>
            </div>
          )}

          {entry.sourceFacts && entry.sourceFacts.length > 0 && (
            <div className="text-[10px]">
              <span className="text-muted-foreground">Source facts:</span>{" "}
              {entry.sourceFacts.map((f, i) => (
                <code key={i} className="bg-muted px-1 rounded mr-1">
                  {f}
                </code>
              ))}
            </div>
          )}

          {entry.missingFacts && entry.missingFacts.length > 0 && (
            <div className="text-[10px] text-amber-600">
              <span className="text-muted-foreground">Missing facts:</span>{" "}
              {entry.missingFacts.map((f, i) => (
                <code key={i} className="bg-amber-50 px-1 rounded mr-1 border border-amber-200">
                  {f}
                </code>
              ))}
            </div>
          )}

          {entry.message && (
            <div className="text-[10px]">
              <span className="text-muted-foreground">Message:</span>{" "}
              {entry.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function getTriStateConfig(triState?: string) {
  switch (triState) {
    case "true":
      return {
        icon: CheckCircle2,
        color: "text-green-600",
        badgeClass: "border-green-200 text-green-700",
      };
    case "false":
      return {
        icon: XCircle,
        color: "text-red-600",
        badgeClass: "border-red-200 text-red-700",
      };
    case "unknown":
      return {
        icon: HelpCircle,
        color: "text-amber-600",
        badgeClass: "border-amber-200 text-amber-700",
      };
    default:
      return {
        icon: HelpCircle,
        color: "text-muted-foreground",
        badgeClass: "",
      };
  }
}

function formatValue(val: unknown): string {
  if (val === null) return "null";
  if (val === true) return "true";
  if (val === false) return "false";
  if (typeof val === "number") return String(val);
  if (typeof val === "string") return val.length > 20 ? val.slice(0, 20) + "…" : val;
  if (typeof val === "object") return JSON.stringify(val).slice(0, 30);
  return String(val);
}
